import { PageShell, GetStarted } from '../components';
import { PageBody, PageHero, Section, FeatureRows } from '../components/page';
import { Film } from '../components/landing/Video';

/**
 * Written to be read during a security review, as a document. Every claim
 * here was checked against the Pugio and platform-core code (inventory of
 * 2026-10-04, see .impeccable/product-truth.md). If a mechanism changes,
 * change this page in the same PR.
 *
 * Deliberately not claimed, because the code does not back it: fail-closed
 * authorization across the board, TLS 1.3, append-only or immutable logs,
 * image signing or Binary Authorization, gVisor, KMS or envelope encryption,
 * a customer-supplied vault, SCIM, PII redaction, any certification.
 * Compliance is left off until the owner publishes the certification work.
 */

const SIGN_IN = [
  {
    title: 'Single sign-on',
    body: 'People sign in through WorkOS, using the authorization code flow with PKCE. Statio then issues its own signed tokens rather than passing the identity provider’s tokens around.',
  },
  {
    title: 'Short-lived tokens',
    body: 'Access tokens last five minutes. Refresh tokens last seven days and are replaced on every use; if an old refresh token is ever presented again, the whole session is revoked.',
  },
  {
    title: 'Sessions you can end',
    body: 'Every session is listed in the dashboard and can be revoked one at a time or all at once. Signing out also ends the session at the identity provider. A revoked session cannot refresh, and its last access token expires within five minutes.',
  },
  {
    title: 'Signed tokens, one algorithm',
    body: 'Tokens are signed with RS256 and carry a key id. Only RS256 is accepted, and services verify signatures against published public keys.',
  },
  {
    title: 'Browser sessions',
    body: 'In the web app, tokens live in HttpOnly, Secure cookies. Every state-changing request needs a CSRF token, and responses send HSTS, deny framing and disable content sniffing.',
  },
];

const DEVICES = [
  {
    title: 'Tokens bound to the device',
    body: 'A person’s laptop enrolls its own signing key, and their access token is bound to it. Each call through the gateway carries a fresh proof signed by that key, covering the method, URL and request body, valid for 60 seconds and usable once. A token copied off the machine cannot call a tool on its own.',
  },
  {
    title: 'Two factors to enroll',
    body: 'Enrolling a device key requires a one-time code from an authenticator app. If the check cannot complete, enrollment is refused.',
  },
  {
    title: 'Revoke a device, end its sessions',
    body: 'Devices are listed alongside sessions. Revoking one ends every session bound to it.',
  },
  {
    title: 'Keys in the keychain',
    body: 'The desktop app keeps the device key and agent secrets in the operating system keychain where one is available. App updates are verified against a signing key pinned in the app before they install.',
  },
];

const AGENTS = [
  {
    title: 'Every agent has its own identity',
    body: 'Agents are their own principals, with their own credentials, status and expiry. An agent never acts on a person’s token, and you can suspend or revoke one without touching anyone else.',
  },
  {
    title: 'Credentials stored as hashes',
    body: 'Agent client secrets are stored only as hashes and compared in constant time. An agent can hold two active credentials, so you can rotate without downtime, then revoke the old one. Agent tokens last five minutes.',
  },
  {
    title: 'Agents need an owner',
    body: 'When an agent’s owner leaves the organization or is demoted, the agent stops getting tokens until a new owner attests to it.',
  },
  {
    title: 'API keys cannot call tools',
    body: 'API keys are for automating the dashboard: scoped per key, stored as hashes, with optional expiry, rotation and revocation. The gateway refuses them for tool calls; only people and agents can use tools.',
  },
];

const ACCESS = [
  {
    title: 'Per tool, not per server',
    body: 'Access is granted to a member or an agent for a server, and individual tools can then be denied or granted on top. A tool-level deny overrides the server grant, so an agent allowed to read a database is not thereby allowed to delete from it.',
  },
  {
    title: 'Admins do not get tools for free',
    body: 'Owners and admins can manage everything, but running a tool always needs an explicit grant. Being an admin is not the same as having the keys.',
  },
  {
    title: 'Checked at the gateway on every call',
    body: (
      <>
        <p>In order, the gateway checks:</p>
        <ol>
          <li>who is calling, and that their token is valid</li>
          <li>which organization the call belongs to</li>
          <li>the device proof, for people</li>
          <li>access to the server, then to the tool</li>
          <li>the call itself, with the scanner below</li>
        </ol>
        <p>A refused call returns a 403 with the reason, and the API behind it is never touched.</p>
      </>
    ),
  },
  {
    title: 'Agents only see what they can use',
    body: 'When an agent asks a server for its tools, the list is filtered to the tools it is allowed to call. Tools that were never registered are blocked.',
  },
  {
    title: 'Separation of duties',
    body: 'Every dashboard action has a required role, and an action without one is refused. Approving a server for production is its own permission, separate from deploying it, and only admins can change where a hosted server may connect.',
  },
];

const SECRETS = [
  {
    title: 'Encrypted before it is stored',
    body: 'Each credential is sealed with AES-256-GCM, with its own random nonce, and bound to its record so encrypted values cannot be swapped between rows. The encryption key is kept in Google Secret Manager, outside the database, so a copy of the database alone yields only ciphertext. Keys are versioned and can be rotated without losing access to what is already stored.',
  },
  {
    title: 'Added at call time',
    body: 'The gateway fetches the credential when a call needs it and adds it to the outgoing request. The agent never sees the raw key, and the caller’s own authorization headers and cookies are stripped before anything goes upstream.',
  },
  {
    title: 'Released only to the gateway',
    body: 'The service that holds credentials hands out credential values only to the gateway’s own service account.',
  },
];

const SCANNING = [
  {
    title: 'Arguments, before the call',
    body: 'Every tool call’s arguments are scanned for shell injection, SQL injection, path traversal, requests aimed at internal addresses and oversized inputs. A finding blocks the call with “Request blocked by security policy”. Scanning is on by default; an organization can switch it to monitor, but not off.',
  },
  {
    title: 'Responses, before the agent',
    body: 'Responses are checked for injected instructions, such as text posing as a system message or asking the agent to send data elsewhere, before they reach the agent. Secrets that appear in a response, including cloud, payment, chat and AI provider keys, private keys and connection strings, are masked before the agent sees them.',
  },
  {
    title: 'Tool descriptions, at registration',
    body: 'When a server is added, its tool names and descriptions are scanned for prompt injection and hidden Unicode characters, and anything suspicious is flagged for an admin to review.',
  },
];

const NETWORK = [
  {
    title: 'No requests to private addresses',
    body: 'The gateway refuses to call anything that resolves to a private, loopback or cloud metadata address, and connects to the exact address it checked, so a DNS change between the check and the call does not get through.',
  },
  {
    title: 'Egress allowlist per hosted server',
    body: 'Hosted servers have no direct route to the internet. Their outbound traffic is routed through Statio’s egress proxy, which identifies each server by its own credentials and checks every destination against that server’s allowlist. Nothing is allowed until you add it, only port 443 is open by default, a bare wildcard is refused, and every blocked attempt is logged.',
  },
  {
    title: 'Limits on everything',
    body: 'Requests are rate limited per address and per organization, with tighter limits on sign-in endpoints. Request bodies are capped at 10 MB, upstream calls time out, and outbound connections require TLS 1.2 or newer.',
  },
];

const SUPPLY_CHAIN = [
  {
    title: 'Hosted servers are private',
    body: 'Servers Statio hosts are not reachable from the internet. Only the gateway can call them, with a signed identity token, and they run under a service account with no other permissions.',
  },
  {
    title: 'Minimal, non-root containers',
    body: 'Generated servers run as a non-root user on a patched Alpine base. Statio’s own services run on distroless, non-root images.',
  },
  {
    title: 'Scanned before they ship',
    body: 'Catalog servers are scanned with Grype and npm audit for known vulnerabilities, and with Semgrep rules that look for environment exfiltration, unexpected outbound calls, dynamic code execution and sensitive file reads. High or critical findings block the deploy, and so does a scanner that fails to run. Container images you bring are scanned with Grype, and critical findings block them.',
  },
  {
    title: 'Our own code',
    body: 'Statio’s dependencies are scanned with Grype on every pull request and daily, against a vulnerability database refreshed each day. Dependabot raises updates weekly, and the Go code is linted with gosec.',
  },
];

const RECORDS = [
  {
    title: 'Activity: every tool call',
    body: 'Each call through the gateway is recorded with the agent or person behind it, the tool and server, the time, the latency, the status code, the client and the source address. Refused calls are recorded too.',
  },
  {
    title: 'Audit logs: every change',
    body: 'Every change to access, servers, secrets, agents and webhooks is recorded with who made it and the result, along with refused attempts. Request and response bodies are never stored in either log.',
  },
  {
    title: 'Clear retention',
    body: 'Tool calls are kept for 30 days, audit logs for 90 days and model calls for 13 months on every plan. An admin can shorten the first two in settings, and Enterprise keeps records for up to two years.',
  },
  {
    title: 'Export and SIEM',
    body: 'On Business and Enterprise, admins export audit logs, tool calls and model calls as CSV or NDJSON files. Tool calls, audit events, or both can be streamed to your security tooling by webhook, signed with HMAC-SHA256 over a timestamp and the body (X-Statio-Signature and X-Statio-Timestamp), so your receiver can verify each one and reject stale deliveries.',
  },
];

const DATA = [
  {
    title: 'In transit and at rest',
    body: 'Traffic to Statio is HTTPS only. Credentials are encrypted by Statio as described above; everything else lives in Google Cloud SQL, encrypted at rest by Google.',
  },
  {
    title: 'Where it lives',
    body: 'The managed service runs on Google Cloud in the us-central1 region.',
  },
  {
    title: 'Deletion',
    body: "Deleting your organization permanently removes its data, audit logs included. Deleting your account removes your devices, connections and keys, and strips your details from the organization's records.",
  },
];

export default function SecurityPage() {
  return (
    <PageShell>
      <PageBody>
        <PageHero
          title="Security at Statio"
          lede="This page is written to be read during a security review. It covers how people and agents prove who they are, where access is decided, what happens to a credential, what the gateway checks on every call, and what gets recorded."
        />
        <div className="pg-film">
          <Film
            name="security"
            label="Statio film: one tool call passing every check at the gateway, and a second call blocked"
            transcript="One tool call, and everything that checks it on the way through. One: who is calling? A short-lived token bound to this laptop; each call carries a fresh proof signed by its key, valid 60 seconds and usable once. Two: may they call this tool? Access is decided per tool, at the gateway, on every call, and a deny on a person or agent always wins. Three: are the arguments safe? Scanned for shell and SQL injection, path traversal, requests aimed at internal addresses and oversized inputs. Four: the gateway adds the Stripe credential at call time from encrypted storage; the agent never sees it. The call reaches the Stripe API. Five: is the response clean? It is checked for injected instructions, and secrets are masked before the agent sees them. Six: Activity records who called what, on which server, how long it took and whether it was allowed. A second call, repos.getContent with ../../etc/passwd, is stopped at the arguments check: Request blocked by security policy. It never reached the API."
          />
        </div>
        <Section id="sign-in" title="Sign-in" lede="Single sign-on, short tokens and sessions you can end." split>
          <FeatureRows items={SIGN_IN} columns={1} />
        </Section>
        <Section id="devices" title="Devices" lede="A stolen token is not enough on its own." split>
          <FeatureRows items={DEVICES} columns={1} />
        </Section>
        <Section id="agents" title="Agents" lede="Their own identity, never a borrowed one." split>
          <FeatureRows items={AGENTS} columns={1} />
        </Section>
        <Section id="access" title="Access" lede="Decided per tool, at the gateway, on every call." split>
          <FeatureRows items={ACCESS} columns={1} />
        </Section>
        <Section
          id="credentials"
          title="Credentials"
          lede="Stored encrypted, added at call time, never handed to agents."
          split
        >
          <FeatureRows items={SECRETS} columns={1} />
        </Section>
        <Section id="scanning" title="Call scanning" lede="What goes in and what comes back are both checked." split>
          <FeatureRows items={SCANNING} columns={1} />
        </Section>
        <Section id="network" title="Network" lede="The gateway decides where traffic may go." split>
          <FeatureRows items={NETWORK} columns={1} />
        </Section>
        <Section
          id="supply-chain"
          title="Hosted servers"
          lede="Private, unprivileged and scanned before they run."
          split
        >
          <FeatureRows items={SUPPLY_CHAIN} columns={1} />
        </Section>
        <Section id="records" title="Records" lede="Two logs, kept 30 and 90 days unless you shorten them." split>
          <FeatureRows items={RECORDS} columns={1} />
        </Section>
        <Section id="data" title="Data" split>
          <FeatureRows items={DATA} columns={1} />
        </Section>
        <Section id="disclosure" title="Responsible disclosure" split>
          <FeatureRows
            columns={1}
            items={[
              {
                title: 'Report a vulnerability',
                body: (
                  <>
                    Email{' '}
                    <a className="s-link" href="mailto:dans@romans.dev">
                      dans@romans.dev
                    </a>
                    . We take every report seriously and respond within 48 hours. We do not pursue legal action against
                    good-faith security researchers.
                  </>
                ),
              },
            ]}
          />
        </Section>
      </PageBody>
      <GetStarted />
    </PageShell>
  );
}
